import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { FreshdeskHelpModalComponent } from './freshdesk-help-modal/freshdesk-help-modal.component';
import { JobLogService } from '../services/job-log.service';
import { flatten } from 'lodash-es';
import { MatchedArticle } from '../interfaces/matched-article.interface';
import { MissingArticleFeedbackComponent } from './missing-article-feedback/missing-article-feedback.component';
import { getErrorSwal } from '@asamco/utils';
import { LinkDetails } from '../interfaces/link-details.interface';

@Component({
	selector: 'assistance-modal',
	standalone: true,
	imports: [
		CommonModule,
		FreshdeskHelpModalComponent,
		MissingArticleFeedbackComponent
	],
	templateUrl: './assistance-modal.component.html',
	styleUrls: ['./support-styles.scss', './assistance-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistanceModalComponent {
	@ViewChild('helpModal') helpModal?: FreshdeskHelpModalComponent;
	@ViewChild('missingArticleFeedback') missingArticleFeedback?: MissingArticleFeedbackComponent;

	constructor(private joblogService: JobLogService) {

	}

	public title: string;
	public errorText: string;

	public link$ = new BehaviorSubject<LinkDetails>(null);

	public articles$: BehaviorSubject<MatchedArticle[]> = new BehaviorSubject<MatchedArticle[]>(null);
	public showDialog$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public showError(title: string, errorText: string) {
		const errorSwal = getErrorSwal(errorText);
		this.show(title,  errorSwal.text);
	}

	public show(title: string, errorText: string) {
		this.title = (title || '') === '' ? 'An error occurred' : title;
		this.errorText = errorText;

		this.articles$.next(null);

		const requests: Observable<MatchedArticle[]>[] = [];
		if((errorText || '') !== '') {
			requests.push(this.joblogService.getMatchedArticles({
				jobLogType: 4,
				message: errorText,
				fromApplicationId: this.link$.value?.fromApplicationId,
				toApplicationId: this.link$.value?.toApplicationId,
				linkType: this.link$.value?.linkTypeId || 0
			}));
		}
		if((title || '') !== '') {
			requests.push(this.joblogService.getMatchedArticles({
				jobLogType: 4,
				message: title,
				fromApplicationId: this.link$.value?.fromApplicationId,
				toApplicationId: this.link$.value?.toApplicationId,
				linkType: this.link$.value?.linkTypeId || 0
			}));
		}

		combineLatest(requests).pipe().subscribe(mas => this.articles$.next(flatten(mas)));

		this.showDialog$.next(true);
	}

	public close(): void {
		this.showDialog$.next(false);
	}

	public showMoreInformation() {
		const origin = {
			kind: 'link_logs' as const,
			linkId: this.link$.value?.id || 0,
			pattern: {
				id: this.articles$.value[0].patternId ?? null, // there can be multiple pertinent pattern Ids??
			},
			logMessage: this.errorText ?? null,
		};

		this.close();

		this.helpModal?.open('Issue help', flatten(this.articles$.value.map(a => a.articleIds)), origin);
	}

	public requestSupport() {
		const origin = {
			kind: 'link_logs' as const,
			linkId: this.link$.value?.id || 0,
			instanceId: null,
			jobLogType: 4,
			jobLogId: null,
			logMessage: this.errorText,
			pattern: { id: null },
		};

		this.close();

		this.missingArticleFeedback?.open(origin);
	}
}
